/**
 * DESIGN TOKENS — single source of truth for the entire UI.
 *
 * Anything visual (color, radius, shadow, motion, typography) MUST be defined here.
 * Consumers:
 *   - src/config/themeBootstrap.ts → injects color tokens as CSS vars (Tailwind reads them)
 *   - src/config/theme.ts          → MUI theme (palette, shape, typography)
 *   - tailwind.config.js           → reads non-color tokens (radii, shadows, motion)
 *
 * To rebrand: change values here. That's it.
 */

// ────────────────────────────────────────────────────────────────────────────
// COLORS — written as raw "R G B" triplets so they can be used with
// `rgb(var(--color-x) / <alpha-value>)` in Tailwind for opacity modifiers.
// ────────────────────────────────────────────────────────────────────────────

// Brand colors:
//   Brand Blue  #194E70  →  25 78 112    (primary action, headings on dark)
//   Brand Teal  #329696  →  50 150 150   (accent, highlights, secondary action)

const lightPalette = {
  background: '250 250 249',
  surface: '255 255 255',
  surfaceMuted: '245 245 244',
  foreground: '23 23 23',
  foregroundMuted: '87 83 78',
  secondary: '120 113 108',
  border: '231 229 228',
  borderStrong: '214 211 209',
  primary: '25 78 112',          // #194E70 — brand blue
  primaryForeground: '250 250 249',
  accent: '50 150 150',           // #329696 — brand teal
  accentForeground: '255 255 255',
  danger: '220 38 38',
  success: '22 163 74',
  warning: '217 119 6',
} as const;

const darkPalette = {
  background: '10 10 10',
  surface: '23 23 23',
  surfaceMuted: '30 30 30',
  foreground: '245 245 244',
  foregroundMuted: '168 162 158',
  secondary: '120 113 108',
  border: '38 38 38',
  borderStrong: '64 64 64',
  primary: '74 143 184',          // brand blue, lifted for dark surfaces
  primaryForeground: '10 10 10',
  accent: '90 200 200',           // brand teal, lifted for dark surfaces
  accentForeground: '10 10 10',
  danger: '248 113 113',
  success: '74 222 128',
  warning: '251 191 36',
} as const;

export type PaletteToken = keyof typeof lightPalette;

// ────────────────────────────────────────────────────────────────────────────
// SHADOWS — same name in light/dark, different alpha.
// ────────────────────────────────────────────────────────────────────────────

const lightShadows = {
  soft: '0 1px 2px rgb(0 0 0 / 0.04), 0 2px 8px rgb(0 0 0 / 0.04)',
  elevated: '0 4px 16px -4px rgb(0 0 0 / 0.08), 0 8px 32px -8px rgb(0 0 0 / 0.08)',
  overlay: '0 24px 48px -12px rgb(0 0 0 / 0.18)',
};

const darkShadows = {
  soft: '0 1px 2px rgb(0 0 0 / 0.4), 0 2px 8px rgb(0 0 0 / 0.3)',
  elevated: '0 4px 16px -4px rgb(0 0 0 / 0.5), 0 8px 32px -8px rgb(0 0 0 / 0.4)',
  overlay: '0 24px 48px -12px rgb(0 0 0 / 0.6)',
};

// ────────────────────────────────────────────────────────────────────────────
// SCALE — radii, spacing, motion, typography. Mode-agnostic.
// ────────────────────────────────────────────────────────────────────────────

export const radii = {
  none: '0px',
  sm: '6px',
  md: '8px',
  lg: '10px',
  xl: '14px',
  '2xl': '20px',
  '4xl': '32px',
  full: '9999px',
} as const;

export const motion = {
  fast: '120ms cubic-bezier(0.16, 1, 0.3, 1)',
  base: '160ms cubic-bezier(0.16, 1, 0.3, 1)',
  slow: '320ms cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

export const typography = {
  sansFamily: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
  displayFamily: ['Instrument Serif', 'Times New Roman', 'serif'],
  monoFamily: ['ui-monospace', 'SFMono-Regular', 'Menlo', 'monospace'],
  baseSize: 16,
  trackingTight: '-0.022em',
  trackingBase: '-0.011em',
} as const;

// ────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ────────────────────────────────────────────────────────────────────────────

export type Mode = 'light' | 'dark';

export const tokens = {
  light: { palette: lightPalette, shadows: lightShadows },
  dark: { palette: darkPalette, shadows: darkShadows },
  radii,
  motion,
  typography,
} as const;

/** Convert "R G B" triplet to "#rrggbb" — used by MUI palette where it needs hex. */
export function rgbToHex(triplet: string): string {
  const [r, g, b] = triplet.split(' ').map(Number);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
